import { useEffect, useState, useMemo } from "react";
import { MoreHorizontal } from "lucide-react";
import { MenuButton } from "~/components/assistant/MenuButton";
import type { PartialSkill } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";

interface SkillsPopupProps {
  systemSkills: PartialSkill[];
  userSkills: PartialSkill[];
  searchFilter?: string;
  onSelectSkill: (skillName: string) => void;
  onAddSkill?: () => void;
  onEditSkill?: (skillId: string) => void;
  onDeleteSkill?: (skillId: string) => void;
  onLearnMore?: () => void;
  enableKeyboardNav?: boolean;
  onClose?: () => void;
}

// Filter skills based on search text
function filterSkills(skills: PartialSkill[], searchText: string) {
  if (!searchText.trim()) {
    return skills;
  }
  const search = searchText.toLowerCase();
  return skills.filter((skill) => skill.name.toLowerCase().includes(search));
}

export function SkillsPopup({
  systemSkills,
  userSkills,
  searchFilter = "",
  onSelectSkill,
  onAddSkill,
  onEditSkill,
  onDeleteSkill,
  onLearnMore,
  enableKeyboardNav = false,
  onClose,
}: SkillsPopupProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredSystemSkills = useMemo(
    () => filterSkills(systemSkills, searchFilter).sort((a, b) => a.name.localeCompare(b.name)),
    [systemSkills, searchFilter]
  );
  const filteredUserSkills = useMemo(
    () => filterSkills(userSkills, searchFilter).sort((a, b) => a.name.localeCompare(b.name)),
    [userSkills, searchFilter]
  );

  // All selectable items (skills + actions)
  const allItems = useMemo(() => {
    const items: Array<{ type: "skill" | "action"; skill?: PartialSkill; action?: string }> = [];
    filteredSystemSkills.forEach((skill) => items.push({ type: "skill", skill }));
    filteredUserSkills.forEach((skill) => items.push({ type: "skill", skill }));
    items.push({ type: "action", action: "add" });
    items.push({ type: "action", action: "help" });
    return items;
  }, [filteredSystemSkills, filteredUserSkills]);

  // Reset selected index when items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [allItems.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!enableKeyboardNav) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % allItems.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + allItems.length) % allItems.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        const item = allItems[selectedIndex];
        if (item.type === "skill" && item.skill) {
          onSelectSkill(item.skill.name);
        } else if (item.type === "action" && item.action === "add") {
          onAddSkill?.();
          onClose?.();
        } else if (item.type === "action" && item.action === "help") {
          onLearnMore?.();
          onClose?.();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enableKeyboardNav, selectedIndex, allItems, onSelectSkill, onClose, onAddSkill, onLearnMore]);

  let currentIndex = 0;

  return (
    <div className="p-2 max-h-[425px] overflow-y-auto">
      {/* System Skills Section */}
      {filteredSystemSkills.length > 0 && (
        <>
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 pb-1">
            System Skills
          </div>
          {filteredSystemSkills.map((skill) => {
            const itemIndex = currentIndex++;
            return (
              <MenuButton
                key={skill.id}
                label={
                  <div className="flex flex-col">
                    <span className="text-gray-900 font-medium">{skill.name}</span>
                    <span className="text-xs text-gray-500 line-clamp-2">{skill.description}</span>
                  </div>
                }
                onClick={() => onSelectSkill(skill.name)}
                isSelected={enableKeyboardNav && selectedIndex === itemIndex}
              />
            );
          })}
        </>
      )}

      {/* Separator */}
      {filteredSystemSkills.length > 0 && filteredUserSkills.length > 0 && (
        <div className="border-t border-gray-200 my-2" />
      )}

      {/* Your Skills Section */}
      {filteredUserSkills.length > 0 && (
        <>
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-2 pb-1">
            Your Skills
          </div>
          {filteredUserSkills.map((skill) => {
            const itemIndex = currentIndex++;
            return (
              <div key={skill.id} className="group relative">
                <MenuButton
                  label={
                    <div className="flex flex-col">
                      <span className="text-gray-900 font-medium">{skill.name}</span>
                      <span className="text-xs text-gray-500 line-clamp-2">
                        {skill.description}
                      </span>
                    </div>
                  }
                  onClick={() => onSelectSkill(skill.name)}
                  isSelected={enableKeyboardNav && selectedIndex === itemIndex}
                  rightContent={
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="size-4 text-gray-600" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-32 p-1" align="end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditSkill?.(skill.id);
                            onClose?.();
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start text-xs text-red-600 hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteSkill?.(skill.id);
                            onClose?.();
                          }}
                        >
                          Delete
                        </Button>
                      </PopoverContent>
                    </Popover>
                  }
                />
              </div>
            );
          })}
        </>
      )}

      {/* No results message */}
      {filteredSystemSkills.length === 0 && filteredUserSkills.length === 0 && (
        <div className="px-4 py-3 text-[13px] text-gray-500 text-center">No skills found</div>
      )}

      {/* Separator before actions */}
      {(filteredSystemSkills.length > 0 || filteredUserSkills.length > 0) && (
        <div className="border-t border-gray-200 my-2" />
      )}

      {/* Bottom Actions */}
      <MenuButton
        label="Add New Skill..."
        onClick={() => {
          onAddSkill?.();
          onClose?.();
        }}
        isSelected={enableKeyboardNav && selectedIndex === currentIndex++}
      />
      <MenuButton
        label="Learn about AI Skills"
        onClick={() => {
          onLearnMore?.();
          onClose?.();
        }}
        isSelected={enableKeyboardNav && selectedIndex === currentIndex++}
      />
    </div>
  );
}
